/**
 * Mojo Up AI Cloud's parts of Settings: the active organisation, the person's profile, and the ways
 * they sign in. Settings stay calm: groups of rows, no artwork.
 */

import { quotaAmountText, type CloudQuotaRow, type CloudSession, type SignInMethod, type SignInProvider } from '@aioe/shared';
import { Envelope } from '@gravity-ui/icons';
import { Input, InputOTP, TextField } from '@heroui/react';
import { useState } from 'react';

import { API_URL, problemText, routes } from '../../api/client.ts';
import { useCloud } from '../../auth/AuthProvider.tsx';
import { cloudSession, startEmailSignIn, startProviderSignIn, verifyEmailCode } from '../../auth/cloudSession.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { Button, ConfirmDialog, ErrorNote, ListBlock, ListGroup, ListRow } from '../ui.tsx';
import { BrandMark } from './BrandMark.tsx';
import { organisationDetail } from './OrganisationSwitcher.tsx';

const PROVIDER_TEXT: Record<SignInMethod['provider'], string> = { google: 'Google', microsoft: 'Microsoft', github: 'GitHub', facebook: 'Facebook', email: 'Email code' };

/** "4 of 10 on Free", "600 a minute on Free", "No limit": one quota in words. */
function quotaText(r: CloudQuotaRow, plan: string): string {
  if (r.limit === null) return r.used === undefined ? 'No limit' : `${quotaAmountText(r.unit, r.used)}, no limit`;
  // A 0 is not on the plan at all, never "none left".
  if (r.limit === 0) return `Not part of ${plan}`;
  const limit = quotaAmountText(r.unit, r.limit);
  return r.used === undefined ? `${limit} on ${plan}` : `${quotaAmountText(r.unit, r.used)} of ${limit} on ${plan}`;
}

export function CloudOrganisationPanel() {
  const cloud = useCloud();
  const id = cloud?.organisation.id;
  const quotas = useLoad(() => (id ? routes.organisationQuotas(id) : Promise.resolve(undefined)), [id]);
  if (!cloud) return null;
  const o = cloud.organisation;
  const q = quotas.data;
  return (
    <>
      <ListGroup id="cloud-organisation" title="Organisation" description="Switch organisation, or create a team, from the name at the top of the sidebar.">
        <ListRow title="Organisation" value={o.name} />
        <ListRow title="Plan" value={organisationDetail(o)} />
        <ListRow title="Service" value={<span className="mono break-all text-xs">{API_URL}</span>} />
      </ListGroup>
      {q ? (
        <ListGroup id="cloud-plan" title={`What ${q.planLabel} allows`}>
          {q.rows.filter((r) => r.unit !== 'perMinute').map((r) => {
            const full = r.limit !== null && r.limit > 0 && r.used !== undefined && r.used >= r.limit;
            return (
              <ListRow
                key={r.key}
                title={r.label}
                value={
                  <span className="tabular-nums">
                    {quotaText(r, q.planLabel)}
                    {full ? <span className="text-warning"> · at the limit</span> : null}
                  </span>
                }
              />
            );
          })}
        </ListGroup>
      ) : null}
      <ErrorNote error={quotas.error} />
    </>
  );
}

const sessionName = (s: CloudSession) => s.device ?? (s.client === 'mobile' ? 'The AI Cloud app' : 'A browser');

/** Where the person is signed in: what, when it was last used, and signing out there. */
function SessionsList() {
  const sessions = useLoad(() => routes.mySessions(), []);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const end = async (id?: string) => {
    setBusy(id ?? 'all');
    setError(undefined);
    try {
      await (id ? routes.endSession(id) : routes.endOtherSessions());
      await sessions.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const list = sessions.data?.sessions ?? [];
  return (
    <ListGroup
      id="sessions"
      title="Where you are signed in"
      description={sessions.data ? `You stay signed in on up to ${sessions.data.limit} browsers and phones; signing in on another ends the oldest.` : undefined}
      actions={
        list.length > 1 ? (
          <Button size="sm" variant="secondary" isPending={busy === 'all'} onPress={() => void end()}>
            Sign out everywhere else
          </Button>
        ) : undefined
      }
    >
      {list.map((s) => (
        <ListRow
          key={s.id}
          title={
            <>
              {sessionName(s)}
              {s.current ? <span className="font-normal text-muted"> · this one</span> : null}
            </>
          }
          description={`Signed in ${timeAgo(s.startedAt)} · last used ${timeAgo(s.lastUsedAt)}`}
          control={
            !s.current ? (
              <Button size="sm" variant="ghost" isPending={busy === s.id} onPress={() => void end(s.id)}>
                Sign out there
              </Button>
            ) : undefined
          }
        />
      ))}
      {error || sessions.error ? (
        <ListBlock>
          <ErrorNote error={error ?? sessions.error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

export function CloudProfilePanel() {
  const cloud = useCloud();
  const [name, setName] = useState(cloud?.me.name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  if (!cloud) return null;
  const changed = name.trim() && name.trim() !== (cloud.me.name ?? '');
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.renameMe(name.trim());
      await cloud.reload();
      setSaved(true);
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ListGroup id="profile" title="Your profile">
        <ListRow title="Name" description="As your team sees it.">
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (changed) void save();
            }}
          >
            <TextField
              className="min-w-0 flex-1 basis-56"
              variant="secondary"
              value={name}
              onChange={(v) => {
                setName(v);
                setSaved(false);
              }}
              aria-label="Your name"
            >
              <Input placeholder="Your name, as your team sees it" maxLength={100} />
            </TextField>
            <Button type="submit" size="sm" variant="secondary" isPending={busy} isDisabled={!changed}>
              {saved && !changed ? 'Saved' : 'Save name'}
            </Button>
          </form>
        </ListRow>
        <ListRow title="Email" value={cloud.me.email ?? 'No email on this account'} />
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
      </ListGroup>
      <SessionsList />
    </>
  );
}

export function SignInMethodsPanel() {
  const cloud = useCloud();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const [removing, setRemoving] = useState<SignInMethod>();
  if (!cloud) return null;
  const methods = cloud.me.signInMethods;
  const remove = async (m: SignInMethod) => {
    setRemoving(undefined);
    setBusy(m.id);
    setError(undefined);
    try {
      await routes.removeSignInMethod(m.id);
      await cloud.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <>
      <ListGroup id="sign-in" title="How you sign in" description={methods.length < 2 ? 'This is your only way to sign in, so it cannot be removed until you add another.' : undefined}>
        {methods.map((m) => (
          <ListRow
            key={m.id}
            icon={m.provider === 'email' ? <Envelope /> : <BrandMark provider={m.provider} />}
            title={PROVIDER_TEXT[m.provider]}
            description={`${m.email ?? 'No email shared'}${m.email && !m.emailVerified ? ' (not verified)' : ''} · last used ${timeAgo(m.lastUsedAt)}`}
            control={
              methods.length > 1 ? (
                <Button size="sm" variant="ghost" isPending={busy === m.id} onPress={() => setRemoving(m)}>
                  Remove
                </Button>
              ) : undefined
            }
          />
        ))}
        <ListBlock>
          <AddSignInMethod linked={new Set(methods.map((m) => m.provider))} />
        </ListBlock>
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
      </ListGroup>
      <ConfirmDialog
        isOpen={!!removing}
        title={removing ? `Stop signing in with ${PROVIDER_TEXT[removing.provider]}` : 'Stop signing in'}
        confirmLabel="Remove"
        danger
        onCancel={() => setRemoving(undefined)}
        onConfirm={() => removing && remove(removing)}
      >
        {removing ? `You will no longer be able to sign in with ${PROVIDER_TEXT[removing.provider]}${removing.email ? ` (${removing.email})` : ''}.` : null}
      </ConfirmDialog>
    </>
  );
}

const PROVIDER_ORDER: Array<Exclude<SignInProvider, 'email'>> = ['google', 'microsoft', 'github', 'facebook'];

/** Another way into this same account: a provider (a round trip, back to Settings) or another email address by code. */
function AddSignInMethod({ linked }: { linked: Set<SignInProvider> }) {
  const cloud = useCloud();
  const offered = useLoad(() => routes.signInProviders(), []);
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string>();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const providers = PROVIDER_ORDER.filter((p) => !linked.has(p) && offered.data?.providers.some((o) => o.id === p));
  const run = async (what: string, fn: () => Promise<void>) => {
    setBusy(what);
    setError(undefined);
    try {
      await fn();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  const addProvider = (p: Exclude<SignInProvider, 'email'>) => run(p, async () => startProviderSignIn(p, '/settings/account', (await routes.linkTicket()).ticket));
  const sendCode = () =>
    run('send', async () => {
      const address = email.trim().toLowerCase();
      await startEmailSignIn(address, '/settings/account', (await routes.linkTicket()).ticket);
      setSentTo(address);
      setCode('');
    });
  const verify = (digits = code) =>
    run('verify', async () => {
      await verifyEmailCode(sentTo!, digits);
      setSentTo(undefined);
      setEmail('');
      await cloud?.reload();
    });
  return (
    <div className="grid gap-3">
      <div className="text-sm font-medium">Add a way to sign in</div>
      {providers.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {providers.map((p) => (
            <Button key={p} size="sm" variant="secondary" isPending={busy === p} onPress={() => void addProvider(p)}>
              <BrandMark provider={p} />
              {PROVIDER_TEXT[p]}
            </Button>
          ))}
        </div>
      )}
      {sentTo ? (
        <form
          className="flex flex-wrap items-center gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.length === 6) void verify();
          }}
        >
          <span className="text-sm text-muted">Code sent to {sentTo}</span>
          <InputOTP aria-label="Six-digit code" maxLength={6} value={code} onChange={(v) => setCode(v.replace(/\D/g, ''))} onComplete={(v: string) => void verify(v)} pattern="^\d+$" inputMode="numeric" autoComplete="one-time-code" autoFocus isDisabled={busy === 'verify'}>
            <InputOTP.Group>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTP.Slot key={i} index={i} />
              ))}
            </InputOTP.Group>
          </InputOTP>
          <Button type="submit" size="sm" variant="secondary" isPending={busy === 'verify'} isDisabled={code.length !== 6}>
            Add address
          </Button>
          <Button size="sm" variant="ghost" onPress={() => setSentTo(undefined)}>
            Cancel
          </Button>
        </form>
      ) : (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) void sendCode();
          }}
        >
          <TextField className="min-w-56 flex-1" variant="secondary" value={email} onChange={setEmail} aria-label="Another email address">
            <Input type="email" placeholder="Another email address" maxLength={254} />
          </TextField>
          <Button type="submit" size="sm" variant="secondary" isPending={busy === 'send'} isDisabled={!email.trim()}>
            <Envelope className="size-3.5" />
            Send a code
          </Button>
        </form>
      )}
      <ErrorNote error={error} />
    </div>
  );
}

/**
 * Deleting the account. Organisations only this person is in go with it, with everything in them;
 * from the rest they leave. Refused while they are a team's only owner and others remain.
 */
export function DeleteAccountPanel() {
  const cloud = useCloud();
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  if (!cloud) return null;
  const expected = cloud.me.email ?? 'delete';
  const remove = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await routes.deleteAccount(confirm.trim());
      cloudSession.forget();
      location.assign('/');
    } catch (e) {
      setError(problemText(e));
      setBusy(false);
    }
  };
  return (
    <ListGroup
      id="delete-account"
      title="Delete your account"
      description="This deletes your AI Cloud account and your personal organisation, with its projects, memory, backups and records. You leave every team you belong to; teams where you are the only person are deleted too. Your workbenches stop syncing. It cannot be undone. If it deletes an organisation only you belong to, we email you a signed certificate saying what was deleted and what remains."
    >
      <ListRow title="Confirm with your email" description={`Type ${expected} to confirm.`}>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (confirm.trim().toLowerCase() === expected.toLowerCase()) void remove();
          }}
        >
          <TextField className="min-w-0 flex-1 basis-56" variant="secondary" value={confirm} onChange={setConfirm} aria-label="Confirm">
            <Input placeholder={`Type ${expected} to confirm`} maxLength={254} />
          </TextField>
          <Button type="submit" size="sm" variant="danger" isPending={busy} isDisabled={confirm.trim().toLowerCase() !== expected.toLowerCase()}>
            Delete account
          </Button>
        </form>
      </ListRow>
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
